import React, { useEffect, useId, useRef, useState } from "react";
import classNames from "classnames";
import { SketchPicker } from "react-color";
import Icon from "../../theme/untitled/icons/Icon";
import Tooltip from "../../theme/untitled/Tooltip/Tooltip";
import styles from "./BrandingPage.module.scss";

const HEX_RE = /^#[0-9a-fA-F]{6}$/;

const ColorRow = ({ title, tooltip, description, value, onChange, disabled }) => {
  const [pickerOpen, setPickerOpen] = useState(false);
  const containerRef = useRef(null);
  // Scopes the swatch's dynamic background to a generated <style> rule
  // instead of a style prop — see [[feedback_no_inline_styles]]. Safe to
  // interpolate raw: only reached once `value` has already passed HEX_RE.
  const dotClass = "color-dot-" + useId().replace(/[^a-zA-Z0-9]/g, "");

  useEffect(() => {
    const onClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setPickerOpen(false);
      }
    };
    document.addEventListener("click", onClickOutside);
    return () => document.removeEventListener("click", onClickOutside);
  }, []);

  const isValid = !value || HEX_RE.test(value);
  const hasColor = isValid && !!value;

  return (
    <div className={styles.row}>
      <div className={styles.rowLabel}>
        <div className={styles.rowTitle}>
          {title}
          {tooltip && (
            <Tooltip content={tooltip}>
              <span className={styles.rowTitleHelp} tabIndex={0}>
                <Icon name="HelpCircle" size={14} />
              </span>
            </Tooltip>
          )}
        </div>
        <div className={styles.rowDescription}>{description}</div>
      </div>

      <div className={styles.control}>
        <div className={styles.swatch} ref={containerRef}>
          <div className={styles.colorPickerPopover}>
            {hasColor && <style>{`.${dotClass}{background:${value}}`}</style>}
            <button
              type="button"
              className={classNames(styles.dot, hasColor && dotClass, {
                [styles["dot--invalid"]]: !isValid,
              })}
              onClick={() => !disabled && setPickerOpen((prev) => !prev)}
              disabled={disabled}
              aria-label={`Choose ${title.toLowerCase()}`}
            />
            {pickerOpen && (
              <div className={styles.colorPickerPanel}>
                <SketchPicker color={value || "#000000"} onChange={(e) => onChange(e.hex)} />
              </div>
            )}
          </div>
          <input
            className={classNames(styles.hexInput, { [styles["hexInput--bad"]]: !isValid })}
            type="text"
            value={value || ""}
            onChange={(e) => onChange(e.target.value)}
            disabled={disabled}
            placeholder="#000000"
          />
        </div>
        {!value && <span className={styles.meta}>Revisual default</span>}
        {value && !isValid && (
          <div className={styles.fieldError}>
            <Icon name="AlertCircle" size={14} className={styles.fieldErrorIcon} />
            <span>Enter a 6-digit hex value, like #1a1a1a.</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default ColorRow;
